import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { rbacAPI, employeeAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, Shield, UserPlus, Trash2, Edit, Key, ShieldCheck, User, Mail } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';

const UsersRoles = () => {
    const { user } = useAuth();
    const { dialogProps, confirm } = useConfirmation();
    const [activeTab, setActiveTab] = useState('roles');
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [modules, setModules] = useState([]);
    const [employees, setEmployees] = useState([]);

    // Role Dialog
    const [isRoleOpen, setIsRoleOpen] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [roleForm, setRoleForm] = useState({
        name: '',
        description: '',
        permissions: [], // Array of {moduleid, create, read, update, delete}
        send_celebration_emails: false,
    });

    // User Dialog
    const [isUserOpen, setIsUserOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userForm, setUserForm] = useState({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role_id: '',
        employee_id: ''
    });

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const companyId = user.company_id;
            const [rolesRes, usersRes, modulesRes, empRes] = await Promise.all([
                rbacAPI.getRoles(companyId),
                rbacAPI.getUsers(companyId),
                rbacAPI.getModules(companyId),
                employeeAPI.getEmployees()
            ]);
            setRoles(rolesRes.data);
            setUsers(usersRes.data);
            setModules(modulesRes.data);
            setEmployees(empRes.data.employees || []);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenRole = (role = null) => {
        if (role) {
            setEditingRole(role);
            const existingPerms = role.permissions || [];
            const permissions = modules.map(m => {
                const existing = existingPerms.find(p => p.moduleid === m);
                return existing || { moduleid: m, read: false, create: false, update: false, delete: false };
            });
            setRoleForm({
                name: role.name,
                description: role.description || '',
                permissions,
                send_celebration_emails: role.send_celebration_emails || false,
            });
        } else {
            setEditingRole(null);
            // Default permissions for all modules
            const defaultPerms = modules.map(m => ({
                moduleid: m, create: false, read: true, update: false, delete: false
            }));
            setRoleForm({ name: '', description: '', permissions: defaultPerms, send_celebration_emails: false });
        }
        setIsRoleOpen(true);
    };

    const handleOpenUser = (u = null) => {
        if (u) {
            setEditingUser(u);
            setUserForm({
                email: u.email,
                password: '',
                first_name: u.first_name,
                last_name: u.last_name,
                role_id: u.role_id,
                employee_id: u.employee_id || ''
            });
        } else {
            setEditingUser(null);
            setUserForm({ email: '', password: '', first_name: '', last_name: '', role_id: '', employee_id: '' });
        }
        setIsUserOpen(true);
    };

    const handlePermissionChange = (moduleid, field, value) => {
        setRoleForm(prev => ({
            ...prev,
            permissions: prev.permissions.map(p =>
                p.moduleid === moduleid ? { ...p, [field]: value } : p
            )
        }));
    };

    const handleToggleColumn = (field, value) => {
        setRoleForm(prev => ({
            ...prev,
            permissions: prev.permissions.map(p => ({ ...p, [field]: value }))
        }));
    };

    const isColumnAllSelected = (field) => {
        if (modules.length === 0) return false;
        return modules.every(moduleid => {
            const perm = roleForm.permissions.find(p => p.moduleid === moduleid);
            return perm && perm[field];
        });
    };

    const handleToggleAllPermissions = (value) => {
        setRoleForm(prev => ({
            ...prev,
            permissions: modules.map(moduleid => {
                const existing = prev.permissions.find(p => p.moduleid === moduleid);
                return { ...(existing || { moduleid }), read: value, create: value, update: value, delete: value };
            })
        }));
    };

    const handleSaveRole = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = { ...roleForm, company_id: user.company_id };
            if (editingRole) {
                await rbacAPI.updateRole(editingRole.id, payload);
                toast.success('Role updated successfully');
            } else {
                await rbacAPI.createRole(payload);
                toast.success('Role created successfully');
            }
            setIsRoleOpen(false);
            const rolesRes = await rbacAPI.getRoles(user.company_id);
            setRoles(rolesRes.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save role');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteRole = async (id) => {
        const confirmed = await confirm({
            title: 'Delete Role',
            description: 'Are you sure you want to delete this role? This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed) return;

        try {
            await rbacAPI.deleteRole(id);
            toast.success('Role deleted');
            setRoles(roles.filter(r => r.id !== id));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete role');
        }
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (!editingUser && userForm.mode === 'employee') {
                // Linking existing employee who "already has a password" (implies existing user account)
                const existingUserForEmp = users.find(u => u.employee_id === userForm.employee_id);

                if (existingUserForEmp) {
                    // Update existing (potentially hidden) user to new role
                    await rbacAPI.updateUser(existingUserForEmp.id, {
                        role_id: userForm.role_id,
                        first_name: userForm.first_name,
                        last_name: userForm.last_name
                    });
                    toast.success('Employee permissions updated successfully');
                } else {
                    toast.error('This employee has no existing user account to link. Please use "New User" to create one with a password.');
                    setSubmitting(false);
                    return;
                }
            } else if (editingUser) {
                // Update existing user from table
                const payload = { ...userForm };
                if (!payload.password) delete payload.password;
                delete payload.mode;
                await rbacAPI.updateUser(editingUser.id, payload);
                toast.success('User updated successfully');
            } else {
                // Create New User Manual
                const payload = { ...userForm, company_id: user.company_id };
                delete payload.mode;
                await rbacAPI.createUser(payload);
                toast.success('User created successfully');
            }

            setIsUserOpen(false);
            const usersRes = await rbacAPI.getUsers(user.company_id);
            setUsers(usersRes.data);
            setUserForm({ email: '', password: '', first_name: '', last_name: '', role_id: '', employee_id: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save user');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteUser = async (id) => {
        const confirmed = await confirm({
            title: 'Delete User',
            description: 'Are you sure you want to delete this user? This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!confirmed) return;

        try {
            await rbacAPI.deleteUser(id);
            toast.success('User deleted successfully');
            setUsers(users.filter(u => u.id !== id));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 lg:p-6 animate-slide-up">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">Users & Roles</h1>
                    <p className="text-muted-foreground">Manage granular permissions and user access for your organization.</p>
                </div>
                <div className="flex gap-2">
                    {activeTab === 'roles' ? (
                        <Button onClick={() => handleOpenRole()} className="shadow-sm">
                            <PlusIcon className="mr-2 h-4 w-4" />
                            New Custom Role
                        </Button>
                    ) : (
                        <Button onClick={() => handleOpenUser()} className="shadow-sm">
                            <UserPlus className="mr-2 h-4 w-4" />
                            Invite User
                        </Button>
                    )}
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="bg-slate-100 p-1 border">
                    <TabsTrigger value="roles" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-6">Roles & Permissions</TabsTrigger>
                    <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:shadow-sm px-6">User Accounts</TabsTrigger>
                </TabsList>

                <TabsContent value="roles" className="space-y-4">
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {roles.filter(r => r.name !== 'Employee').map(role => (
                            <Card key={role.id} className="hover:border-primary/50 transition-all duration-300 shadow-sm border-slate-200">
                                <CardHeader className="pb-3">
                                    <div className="flex justify-between items-start">
                                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                                            <Shield className="h-5 w-5" />
                                        </div>
                                        <div className="flex gap-1">
                                            {!role.is_system && (
                                                <>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenRole(role)}>
                                                        <Edit className="h-4 w-4 text-slate-500" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteRole(role.id)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </>
                                            )}
                                            {role.is_system && (
                                                <Badge variant="secondary" className="bg-blue-50 text-blue-600 border-blue-100 text-[10px] px-1.5 py-0">System</Badge>
                                            )}
                                        </div>
                                    </div>
                                    <CardTitle className="text-xl font-semibold">{role.name}</CardTitle>
                                    <CardDescription className="line-clamp-2 min-h-[40px]">{role.description || 'No description provided.'}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        <div className="flex flex-wrap gap-1.5">
                                            {role.permissions?.slice(0, 3).map(p => (
                                                <Badge key={p.moduleid} variant="secondary" className="bg-slate-50 text-slate-600 border-slate-200">
                                                    {p.moduleid}
                                                </Badge>
                                            ))}
                                            {role.permissions?.length > 3 && (
                                                <Badge variant="secondary" className="bg-slate-50 text-slate-600 border-slate-200">
                                                    +{role.permissions.length - 3} more
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="pt-2 border-t flex items-center justify-between text-xs text-muted-foreground">
                                            <span>Permissions Mapped</span>
                                            <span className="font-medium text-slate-900">{role.permissions?.length} Modules</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="users" className="space-y-4">
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <Table>
                            <TableHeader className="bg-slate-50/50">
                                <TableRow>
                                    <TableHead className="font-semibold">User</TableHead>
                                    <TableHead className="font-semibold">Role</TableHead>
                                    <TableHead className="font-semibold">Linked Employee</TableHead>
                                    <TableHead className="font-semibold text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.filter(u => {
                                    const roleName = roles.find(r => r.id === u.role_id)?.name;
                                    return roleName !== 'Employee';
                                }).map(u => (
                                    <TableRow key={u.id} className="group hover:bg-slate-50/50 transition-colors">
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                                                    <User className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-slate-900">{u.first_name} {u.last_name}</p>
                                                    <p className="text-xs text-muted-foreground">{u.email}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {u.roles && u.roles.length > 0 ? (
                                                    u.roles.map((roleName, index) => (
                                                        <Badge key={index} variant="outline" className="font-normal border-slate-300 capitalize">
                                                            {roleName.replace(/_/g, ' ').toLowerCase()}
                                                        </Badge>
                                                    ))
                                                ) : (
                                                    <Badge variant="outline" className="font-normal border-slate-300">
                                                        {roles.find(r => r.id === u.role_id)?.name || u.role_id}
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {u.employee_id ? (
                                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                                    <ShieldCheck className="h-4 w-4 text-green-500" />
                                                    {employees.find(e => e.id === u.employee_id)?.employee_code || u.employee_id}
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground text-sm italic">Not linked</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button variant="ghost" size="sm" onClick={() => handleOpenUser(u)}>
                                                    Manage
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className={u.is_system ? "text-slate-300 cursor-not-allowed" : "text-destructive hover:text-destructive"}
                                                    onClick={() => !u.is_system && handleDeleteUser(u.id)}
                                                    disabled={u.is_system}
                                                    title={u.is_system ? "System user cannot be deleted" : "Delete User"}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Role Dialog */}
            <Dialog open={isRoleOpen} onOpenChange={setIsRoleOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingRole ? 'Edit Role' : 'Create Custom Role'}</DialogTitle>
                        <DialogDescription>Define what this role can see and do across modules.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSaveRole} className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Role Name</Label>
                                <Input
                                    value={roleForm.name}
                                    onChange={e => setRoleForm({ ...roleForm, name: e.target.value })}
                                    placeholder="e.g. Finance Auditor"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Input
                                    value={roleForm.description}
                                    onChange={e => setRoleForm({ ...roleForm, description: e.target.value })}
                                    placeholder="Briefly describe what this role does"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
                            <Checkbox
                                id="send_celebration_emails"
                                checked={roleForm.send_celebration_emails}
                                onCheckedChange={val => setRoleForm({ ...roleForm, send_celebration_emails: val })}
                            />
                            <div className="flex flex-col gap-0.5">
                                <Label htmlFor="send_celebration_emails" className="flex items-center gap-2 cursor-pointer font-medium">
                                    <Mail className="h-4 w-4 text-primary" />
                                    Send Daily Celebration Emails
                                </Label>
                                <span className="text-xs text-slate-500">
                                    Users with this role will receive a daily email at 12:00 AM listing today's birthdays and work anniversaries.
                                </span>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-lg font-semibold flex items-center gap-2 text-slate-800">
                                    <Key className="h-5 w-5 text-primary" />
                                    Module Permissions
                                </Label>
                                <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
                                    <Checkbox
                                        id="select-all-permissions"
                                        checked={modules.length > 0 && modules.every(moduleid => { const p = roleForm.permissions.find(x => x.moduleid === moduleid); return p && p.read && p.create && p.update && p.delete; })}
                                        onCheckedChange={handleToggleAllPermissions}
                                    />
                                    <Label htmlFor="select-all-permissions" className="text-sm font-medium cursor-pointer">Select All Permissions</Label>
                                </div>
                            </div>
                            <div className="border rounded-xl overflow-hidden shadow-sm">
                                <Table>
                                    <TableHeader className="bg-slate-50/50">
                                        <TableRow>
                                            <TableHead className="w-[200px] font-semibold">
                                                <span>Module</span>
                                            </TableHead>
                                            <TableHead className="text-center font-semibold">
                                                <div className="flex flex-col items-center gap-1.5 py-2">
                                                    <span>Read</span>
                                                    <Checkbox
                                                        checked={isColumnAllSelected('read')}
                                                        onCheckedChange={(val) => handleToggleColumn('read', val)}
                                                    />
                                                </div>
                                            </TableHead>
                                            <TableHead className="text-center font-semibold">
                                                <div className="flex flex-col items-center gap-1.5 py-2">
                                                    <span>Create</span>
                                                    <Checkbox
                                                        checked={isColumnAllSelected('create')}
                                                        onCheckedChange={(val) => handleToggleColumn('create', val)}
                                                    />
                                                </div>
                                            </TableHead>
                                            <TableHead className="text-center font-semibold">
                                                <div className="flex flex-col items-center gap-1.5 py-2">
                                                    <span>Update</span>
                                                    <Checkbox
                                                        checked={isColumnAllSelected('update')}
                                                        onCheckedChange={(val) => handleToggleColumn('update', val)}
                                                    />
                                                </div>
                                            </TableHead>
                                            <TableHead className="text-center font-semibold">
                                                <div className="flex flex-col items-center gap-1.5 py-2">
                                                    <span>Delete</span>
                                                    <Checkbox
                                                        checked={isColumnAllSelected('delete')}
                                                        onCheckedChange={(val) => handleToggleColumn('delete', val)}
                                                    />
                                                </div>
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {modules.map(moduleid => {
                                            const perm = roleForm.permissions.find(p => p.moduleid === moduleid) || { read: false, create: false, update: false, delete: false };
                                            return (
                                                <TableRow key={moduleid} className="hover:bg-slate-50/30">
                                                    <TableCell className="font-medium capitalize">{moduleid.replace('_', ' ')}</TableCell>
                                                    <TableCell className="p-0">
                                                        <div className="flex justify-center items-center h-12">
                                                            <Checkbox
                                                                checked={perm.read}
                                                                onCheckedChange={(val) => handlePermissionChange(moduleid, 'read', val)}
                                                            />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="p-0">
                                                        <div className="flex justify-center items-center h-12">
                                                            <Checkbox
                                                                checked={perm.create}
                                                                onCheckedChange={(val) => handlePermissionChange(moduleid, 'create', val)}
                                                            />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="p-0">
                                                        <div className="flex justify-center items-center h-12">
                                                            <Checkbox
                                                                checked={perm.update}
                                                                onCheckedChange={(val) => handlePermissionChange(moduleid, 'update', val)}
                                                            />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="p-0">
                                                        <div className="flex justify-center items-center h-12">
                                                            <Checkbox
                                                                checked={perm.delete}
                                                                onCheckedChange={(val) => handlePermissionChange(moduleid, 'delete', val)}
                                                            />
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>

                        <DialogFooter className="bg-slate-50 p-4 -mx-6 -mb-6 border-t mt-6">
                            <Button type="button" variant="outline" onClick={() => setIsRoleOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={submitting} className="min-w-[120px]">
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <SaveIcon className="h-4 w-4 mr-2" />}
                                {editingRole ? 'Update Role' : 'Create Role'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* User Dialog */}
            <Dialog open={isUserOpen} onOpenChange={setIsUserOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
                        <DialogDescription>
                            {editingUser ? 'Update user details and access level.' : 'Add a new user or grant access to an existing employee.'}
                        </DialogDescription>
                    </DialogHeader>

                    {!editingUser && (
                        <Tabs defaultValue="manual" className="w-full mb-4" onValueChange={val => setUserForm(prev => ({ ...prev, mode: val, email: '', password: '', first_name: '', last_name: '', employee_id: '' }))}>
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="manual">New User</TabsTrigger>
                                <TabsTrigger value="employee">From Employee List</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    )}

                    <form onSubmit={handleSaveUser} className="space-y-4">
                        {(!userForm.mode || userForm.mode === 'manual' || editingUser) && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>First Name</Label>
                                        <Input
                                            value={userForm.first_name}
                                            onChange={e => setUserForm({ ...userForm, first_name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Last Name</Label>
                                        <Input
                                            value={userForm.last_name}
                                            onChange={e => setUserForm({ ...userForm, last_name: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Email Address</Label>
                                    <Input
                                        type="email"
                                        value={userForm.email}
                                        onChange={e => setUserForm({ ...userForm, email: e.target.value })}
                                        required
                                        disabled={!!editingUser}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>{editingUser ? 'New Password (Optional)' : 'Password'}</Label>
                                    <Input
                                        type="password"
                                        value={userForm.password}
                                        onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                                        required={!editingUser}
                                        placeholder={editingUser ? 'Leave blank to keep current' : ''}
                                    />
                                </div>
                            </>
                        )}

                        {userForm.mode === 'employee' && !editingUser && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Select Employee</Label>
                                    <Select
                                        value={userForm.employee_id}
                                        onValueChange={val => {
                                            const emp = employees.find(e => e.id === val);
                                            setUserForm({
                                                ...userForm,
                                                employee_id: val,
                                                first_name: emp?.first_name || '',
                                                last_name: emp?.last_name || '',
                                                email: emp?.email || ''
                                            });
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Search employee..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {employees.map(e => (
                                                <SelectItem key={e.id} value={e.id}>
                                                    {e.first_name} {e.last_name} ({e.employee_code})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {userForm.employee_id && (
                                    <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded-md border text-center">
                                        Selected: <span className="font-medium text-slate-900">{userForm.first_name} {userForm.last_name}</span>
                                        <br />
                                        <span className="text-xs">{userForm.email}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Assign Role</Label>
                            <Select
                                value={userForm.role_id}
                                onValueChange={val => setUserForm({ ...userForm, role_id: val })}
                                required
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Role" />
                                </SelectTrigger>
                                <SelectContent>
                                    {roles.filter(r => r.name !== 'Employee').map(r => (
                                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <DialogFooter className="pt-6">
                            <Button type="button" variant="outline" onClick={() => setIsUserOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                {editingUser ? 'Update User' : (userForm.mode === 'employee' ? 'Grant Access' : 'Create User')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            <ConfirmationDialog {...dialogProps} />
        </div >
    );
};

const PlusIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" />
        <path d="M12 5v14" />
    </svg>
);

const SaveIcon = (props) => (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
    </svg>
);

export default UsersRoles;
